import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBusy } from "../../../context/BusyContext";
import { useMessageModal } from "../../contexts/MessageModalContext";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "../../styles/emailBodyEditor.css";

export default function AdminPolicyEditor() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const navigate = useNavigate();
  const { runBusy } = useBusy();
  const { showModal } = useMessageModal();
  const { id } = useParams();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("active");

  useEffect(() => {
    if (!id) return;

    const fetchPolicy = async () => {
      try {
        const res = await fetch(`${BASE_URL}/admin/settings/policies/${id}`, {
          credentials: "include",
        });

        const result = await res.json();

        if (result.success) {
          setTitle(result.data.title);
          setStatus(result.data.status);

          if (quillRef.current) {
            quillRef.current.root.innerHTML = result.data.body;
          }
        }
      } catch (error) {
        console.error("Fetch Policy Error:", error);
      }
    };

    fetchPolicy();
  }, [id]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          [{ align: [] }],
          ["clean"],
        ],
      },
    });
  }, []);

  // const handleSave = async () => {
  //   const html = quillRef.current?.root.innerHTML;

  //   try {
  //     const method = id ? "PUT" : "POST";

  //     const url = id
  //       ? `${BASE_URL}/admin/settings/policies/${id}`
  //       : `${BASE_URL}/admin/settings/policies`;

  //     const res = await fetch(url, {
  //       method,
  //       headers: { "Content-Type": "application/json" },
  //       credentials: "include",
  //       body: JSON.stringify({
  //         title,
  //         body: html,
  //         status,
  //       }),
  //     });

  //     const result = await res.json();

  //     if (result.success) {
  //       showModal({
  //         type: "success",
  //         title: "Success",
  //         message: id
  //           ? "Policy updated successfully."
  //           : "Policy created successfully.",
  //         confirmText: "OK",
  //         onConfirm: () => navigate("/admin/policies"),
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Save Policy Error:", error);

  //     showModal({
  //       type: "error",
  //       title: "Error",
  //       message: "Failed to save policy.",
  //       confirmText: "OK",
  //     });
  //   }
  // };

  const handleSave = async () => {
    const html = quillRef.current?.root.innerHTML;

    await runBusy(async () => {
      try {
        const method = id ? "PUT" : "POST";

        const url = id
          ? `${BASE_URL}/admin/settings/policies/${id}`
          : `${BASE_URL}/admin/settings/policies`;

        const res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            title,
            body: html,
            status,
          }),
        });

        const result = await res.json();

        if (!res.ok || result.success === false) {
          showModal({
            type: "error",
            title: "Error",
            message: result.message || "Failed to save policy.",
            confirmText: "OK",
          });
          return;
        }

        showModal({
          type: "success",
          title: "Success",
          message:
            result.message ||
            (id
              ? "Policy updated successfully."
              : "Policy created successfully."),
          confirmText: "OK",
          onConfirm: () => navigate("/admin/policies"),
        });
      } catch (error) {
        console.error("Save Policy Error:", error);

        showModal({
          type: "error",
          title: "Error",
          message: "Failed to save policy.",
          confirmText: "OK",
        });
      }
    });
  };

  return (
    <div className="email-editor-page">
      <h2>{id ? "Edit Policy" : "Create Policy"}</h2>

      <div className="editor-form">
        <div className="form-group">
          <label>Policy Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Example: Privacy Policy"
          />
        </div>

        <div className="form-group">
          <label>Policy Body</label>

          <div
            ref={editorRef}
            style={{
              height: "400px",
              background: "#fff",
              marginBottom: "30px",
            }}
          />
        </div>

        <div className="form-group">
          <label>Status</label>

          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="active"
                checked={status === "active"}
                onChange={() => setStatus("active")}
              />
              Active
            </label>

            <label>
              <input
                type="radio"
                value="inactive"
                checked={status === "inactive"}
                onChange={() => setStatus("inactive")}
              />
              Inactive
            </label>
          </div>
        </div>

        <div className="editor-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate("/admin/policies")}
          >
            Cancel
          </button>

          <button className="btn-primary" onClick={handleSave}>
            Save Policy
          </button>
        </div>
      </div>
    </div>
  );
}
